import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';

import {useStore, useUnit} from 'effector-react';
import debounce from 'lodash/debounce';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory} from 'react-router-dom';

import {PRIVATE_ROUTES} from '@/app/routing';
import {$subscriptionStatus} from '@/entities/subscriptions/model';
import {SubscriptionStatusEnum} from '@/entities/subscriptions/types';
import {userModel} from '@/entities/user/model';
import {TAXPAYER_ROLES} from '@/entities/user/permissions';
import {PermissionAccess} from '@/features/PermissionAccess';
import {Button, ButtonVariant} from '@/shared/ui/Button';
import {invoicesModel} from '@entities/invoices/model';
import {Input} from '@shared/ui/Input';

import {CancelIcon, Root} from './styled';

export const Control = () => {
    const invoices = useUnit(invoicesModel);
    const history = useHistory();
    const profile = useUnit(userModel);
    const subscriptionStatus = useStore($subscriptionStatus);

    const {t} = useTranslation();
    const [value, setValue] = useState(invoices.state.search);

    useEffect(() => {
        setValue(invoices.state.search);
    }, [invoices.state.search]);

    const debouncedSearchChanged = useMemo(
        () =>
            debounce((nextValue: string, prevValue: string) => {
                if (nextValue !== prevValue) {
                    invoices.searchChanged(nextValue);
                }
            }, 1000),
        [],
    );

    useEffect(() => {
        debouncedSearchChanged(value, invoices.state.search);
        return debouncedSearchChanged.cancel;
    }, [value, invoices.state.search]);

    return (
        <Root>
            <div style={{minWidth: 350, maxWidth: 700}}>
                <Input
                    value={value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
                    placeholder={t('page_invoices.search')}
                    adornment={
                        <CancelIcon
                            width={10}
                            height={10}
                            name="cross"
                            onClick={() => setValue('')}
                        />
                    }
                />
            </div>
            <PermissionAccess roles={TAXPAYER_ROLES}>
                <div>
                    <Button
                        data-for="tooltip-create-invoice"
                        type="button"
                        variant={ButtonVariant.Normal}
                        onClick={async () => {
                            if (subscriptionStatus?.status !== SubscriptionStatusEnum.ACTIVE) {
                                history.push(PRIVATE_ROUTES.SUBSCRIPTIONS);
                                return;
                            }

                            try {
                                const tin = await invoices.generateTinFx({
                                    tin: profile.state.profile?.company.tin ?? '',
                                });
                                const setPath = generatePath(
                                    PRIVATE_ROUTES.DOCUMENTS_CREATE_INVOICE_ID,
                                    {
                                        invoiceId: tin,
                                    },
                                );
                                history.push(setPath, {from: location.pathname});
                            } catch (err) {
                                console.log(err);
                            }
                        }}
                    >
                        Create Invoice
                    </Button>
                </div>
            </PermissionAccess>
        </Root>
    );
};
